import { Component, computed, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  School,
  SchoolDocument,
  getVisibleDocuments,
} from '@awo-care/shared-core';

let nextInstanceId = 0;

@Component({
  selector: 'lib-school-document-download',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  template: `
    @if (showCard()) {
      <mat-card appearance="outlined" class="documents-card">
        <mat-card-header>
          <mat-icon mat-card-avatar>folder</mat-icon>
          <mat-card-title i18n="@@school-documents.title">
            Dokumente
          </mat-card-title>
        </mat-card-header>

        <mat-card-content>
          <ul class="document-list">
            @for (doc of visibleDocuments(); track doc.id) {
              <li class="document-item">
                <div class="document-info">
                  <mat-icon class="file-icon">picture_as_pdf</mat-icon>
                  <span class="title" [id]="idPrefix + '-doc-' + doc.id">{{ doc.title }}</span>
                  <span class="filesize">{{ formatFileSize(doc.fileSize) }}</span>
                </div>
                <button
                  mat-stroked-button
                  color="primary"
                  (click)="download(doc)"
                  class="download-btn"
                  [attr.aria-describedby]="idPrefix + '-doc-' + doc.id"
                >
                  <mat-icon>download</mat-icon>
                  <span i18n="@@school-document.download">Herunterladen</span>
                </button>
              </li>
            }

            @if (extraDocumentLabel(); as extraLabel) {
              <li class="document-item">
                <div class="document-info">
                  <mat-icon class="file-icon">picture_as_pdf</mat-icon>
                  <span class="title" [id]="idPrefix + '-extra'">{{ extraLabel }}</span>
                </div>
                <button
                  mat-stroked-button
                  color="primary"
                  class="download-btn"
                  [attr.data-testid]="extraDocumentTestId()"
                  [attr.aria-describedby]="idPrefix + '-extra'"
                  [disabled]="extraDocumentDownloading()"
                  (click)="extraDocumentDownload.emit()"
                >
                  @if (extraDocumentDownloading()) {
                  <mat-spinner diameter="18" class="button-spinner" />
                  } @else {
                  <mat-icon>download</mat-icon>
                  }
                  <span i18n="@@school-document.download">Herunterladen</span>
                </button>
              </li>
            }

            @for (doc of staticDocuments(); track doc.id) {
              <li class="document-item">
                <div class="document-info">
                  <mat-icon class="file-icon">picture_as_pdf</mat-icon>
                  <span class="title" [id]="idPrefix + '-static-' + doc.id">{{ doc.title }}</span>
                </div>
                <a
                  mat-stroked-button
                  color="primary"
                  class="download-btn"
                  [attr.data-testid]="doc.testId ?? null"
                  [attr.aria-describedby]="idPrefix + '-static-' + doc.id"
                  [href]="doc.url"
                  target="_blank"
                  rel="noopener"
                >
                  <mat-icon>download</mat-icon>
                  <span i18n="@@school-document.download">Herunterladen</span>
                </a>
              </li>
            }
          </ul>
        </mat-card-content>
      </mat-card>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .documents-card {
        margin-bottom: 2rem;
        background: #fff;
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, 0.12);
      }
      .documents-card mat-card-title {
        font-size: 1.1rem;
        font-weight: 500;
      }
      .document-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: 0.75rem;
      }
      .document-item {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 0.5rem;
        padding: 0.75rem 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);

        &:last-child { border-bottom: none; }
      }
      .document-info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        min-width: 0;
      }
      .file-icon {
        color: #d32f2f;
        font-size: 20px;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }
      .title {
        font-weight: 500;
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;
      }
      .filesize {
        font-size: 0.875rem;
        color: rgba(0, 0, 0, 0.6);
        flex-shrink: 0;
      }
      .download-btn {
        align-self: flex-start;
        mat-icon { font-size: 18px; width: 18px; height: 18px; }
      }
      .download-btn .button-spinner {
        display: inline-block;
        margin-right: 8px;
      }
    `,
  ],
})
export class SchoolDocumentDownloadComponent {
  /** Eindeutige ID-Basis je Instanz: Der Dokumenttitel beschreibt den Button, sonst sagt der Screenreader nur „Herunterladen" (#1222). */
  protected readonly idPrefix = `school-doc-${nextInstanceId++}`;

  school = input<School | null>(null);

  /**
   * Optionales Zusatz-Dokument, das als weitere Zeile in dieselbe Dokumente-Karte
   * gerendert wird (z.B. die Self-Service-Bestätigung, #752). `null` = keine Zeile.
   * Download-Logik bleibt beim Aufrufer (Output), damit die Lib generisch bleibt.
   */
  extraDocumentLabel = input<string | null>(null);
  extraDocumentDownloading = input<boolean>(false);
  extraDocumentTestId = input<string | null>(null);
  extraDocumentDownload = output<void>();

  /**
   * Statische Zusatz-Dokumente ohne Storage-Backing (z.B. die Notfallmedikament-
   * Formulare, #914) — direkter Link statt Download-Handler.
   */
  staticDocuments = input<
    { id: string; title: string; url: string; testId?: string }[]
  >([]);

  visibleDocuments = computed<SchoolDocument[]>(() => {
    const s = this.school();
    return s ? getVisibleDocuments(s) : [];
  });

  /** Karte zeigen, sobald es Schuldokumente, ein Zusatz- oder statisches Dokument gibt. */
  protected readonly showCard = computed(
    () =>
      this.visibleDocuments().length > 0 ||
      !!this.extraDocumentLabel() ||
      this.staticDocuments().length > 0
  );

  download(doc: SchoolDocument): void {
    if (doc.downloadUrl) {
      window.open(doc.downloadUrl, '_blank');
    }
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  }
}
